// src/pages/DocViewer.js
// Read-only page for a club document, addressed by slug.
//
// Route: /docs/:slug
//
// Audience model:
//   • Docs carry `audience: string[]` with values from the set
//     ['everyone', 'parents', 'adults', 'coaches', 'admins'].
//   • If the doc includes 'everyone' it's publicly readable (no auth).
//   • Otherwise the viewer must match at least one of the doc's tags.
//   • If the viewer doesn't match (or isn't signed in for a restricted doc),
//     the page renders the same "not found" UI used for missing slugs.
//
// We listen to auth state passively so the page works for both anonymous
// users (everyone docs) and signed-in users (full audience match). No
// redirect to /login — restricted docs simply 404 from the visitor's view.

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, limit, doc as fsDoc, getDoc } from 'firebase/firestore';
import { isActiveMember } from '../utils/membership';
import { onAuthStateChanged } from 'firebase/auth';
import { db, auth } from '../firebase';
import {
  Box, Container, Paper, Typography, CircularProgress, Alert, Button,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Layout from '../components/Layout';
import { useBranding } from '../TournamentBrandingContext';
import { logError } from '../utils/errorLogger';

// Resolve the viewer's audience tags from their user doc.
// Reads the user doc once and derives tags from `roles` array and the
// `isParent` boolean (maintained by the syncParentFlag Cloud Function).
// Returns a Set so the doc-vs-viewer audience check is a simple .some() call.
async function resolveViewerAudiences(user) {
  // 'everyone' is implicit for every visitor (signed in or not). The page
  // can then form queries with array-contains-any that include 'everyone'
  // alongside the user's role-derived tags, so public docs always match.
  const tags = new Set(['everyone']);
  if (!user) return tags;
  try {
    const uDoc = await getDoc(fsDoc(db, 'users', user.uid));
    if (!uDoc.exists()) return tags;
    const data = uDoc.data();
    const roles = data.roles || [];
    if (roles.includes('admin')) tags.add('admins');
    if (roles.includes('coach') || roles.includes('assistant_coach')) tags.add('coaches');
    // Membership comes from the map, not the roles array — day passes no longer
    // write a 'member' role, and a lapsed one would leave it behind anyway.
    if (roles.includes('adult_athlete') || roles.includes('player')
        || roles.includes('member') || isActiveMember(data)) {
      tags.add('adults');
    }
    if (data.isParent === true) tags.add('parents');
  } catch (err) {
    logError('DocViewer', 'audience resolve failed: ' + err.message);
  }
  return tags;
}

export default function DocViewer() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const branding = useBranding();
  const [user, setUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [loading, setLoading] = useState(true);
  const [doc, setDoc] = useState(null);
  const [error, setError] = useState('');

  // Fall back to the same defaults AdminBranding uses if context is empty.
  const primary = branding?.primaryColor || '#1E3A5F';

  // ── Auth subscription (passive — not a gate) ───────────────────────────
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u);
      setAuthChecked(true);
    });
    return unsub;
  }, []);

  // ── Load doc by slug, then verify audience ─────────────────────────────
  useEffect(() => {
    if (!slug || !authChecked) return;
    let cancelled = false;
    (async () => {
      setLoading(true); setError('');
      // ── DIAG ────────────────────────────────────────────────────────────
      console.log('[DocView] step 1 — auth state:', {
        hasUser: !!user, uid: user?.uid, email: user?.email, slug,
      });
      try {
        const viewerTags = await resolveViewerAudiences(user);
        const isAdmin = viewerTags.has('admins');
        console.log('[DocView] step 2 — viewer audience tags:', {
          tags: [...viewerTags], isAdmin,
        });

        let q;
        if (isAdmin) {
          q = query(
            collection(db, 'clubDocuments'),
            where('slug', '==', slug),
            where('enabled', '==', true),
            limit(1),
          );
          console.log('[DocView] step 3 — admin query (slug + enabled)');
        } else if (viewerTags.size > 0) {
          q = query(
            collection(db, 'clubDocuments'),
            where('slug', '==', slug),
            where('enabled', '==', true),
            where('audience', 'array-contains-any', [...viewerTags]),
            limit(1),
          );
          console.log('[DocView] step 3 — non-admin query:', {
            constraints: [
              `slug == ${slug}`,
              'enabled == true',
              `audience array-contains-any ${JSON.stringify([...viewerTags])}`,
            ],
          });
        } else {
          console.log('[DocView] viewerTags empty — abort');
          setDoc(null); setLoading(false); return;
        }
        const snap = await getDocs(q);
        if (cancelled) return;
        console.log('[DocView] step 4 — query succeeded. size:', snap.size);
        if (snap.empty) { setDoc(null); setLoading(false); return; }
        const d = snap.docs[0];
        const data = { id: d.id, ...d.data() };
        setDoc(data);
      } catch (err) {
        console.error('[DocView] FAILED at query/filter step:', {
          message: err?.message,
          code: err?.code,
          name: err?.name,
          full: err,
        });
        logError('DocViewer', err.message, err.stack);
        setError("Couldn't load this document. Try again in a moment.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug, authChecked, user]);

  // ── Render ─────────────────────────────────────────────────────────────
  return (
    <Layout>
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)}
          sx={{ mb: 2, color: primary }}>
          Back
        </Button>
        <Paper sx={{ p: { xs: 2, sm: 4 }, borderTop: `4px solid ${primary}` }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress sx={{ color: primary }} />
            </Box>
          ) : error ? (
            <Alert severity="error">{error}</Alert>
          ) : !doc ? (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <Typography variant="h5" sx={{ mb: 1, color: primary }}>Document not found</Typography>
              <Typography variant="body2" sx={{ color: '#888' }}>
                The link may be incorrect, or the document is no longer available.
              </Typography>
            </Box>
          ) : (
            <>
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, color: primary }}>
                {doc.title || doc.name || 'Document'}
              </Typography>
              {doc.description && (
                <Typography variant="body2" sx={{ color: '#666', mb: 3 }}>
                  {doc.description}
                </Typography>
              )}
              {/* Document body — HTML produced by the admin Quill editor. */}
              <Box
                sx={{
                  '& img': { maxWidth: '100%', height: 'auto' },
                  '& a': { color: primary },
                  lineHeight: 1.6,
                }}
                dangerouslySetInnerHTML={{ __html: doc.content || '' }}
              />
            </>
          )}
        </Paper>
      </Container>
    </Layout>
  );
}
